<script setup>
import { ref } from 'vue'
import PanelOrganic from '@/design/primitives/PanelOrganic.vue'
import ButtonSeed from '@/design/primitives/ButtonSeed.vue'
import FieldLeaf from '@/design/primitives/FieldLeaf.vue'
import CardLichen from '@/design/primitives/CardLichen.vue'
import HeadlineFraunces from '@/design/primitives/HeadlineFraunces.vue'
import CoinGlyph from '@/design/primitives/CoinGlyph.vue'

import IconLeaf from '@/design/icons/IconLeaf.vue'
import IconFossil from '@/design/icons/IconFossil.vue'
import IconDrop from '@/design/icons/IconDrop.vue'
import IconTendril from '@/design/icons/IconTendril.vue'
import IconShield from '@/design/icons/IconShield.vue'
import IconArrowRight from '@/design/icons/IconArrowRight.vue'
import IconCopy from '@/design/icons/IconCopy.vue'
import IconCheck from '@/design/icons/IconCheck.vue'
import IconSearch from '@/design/icons/IconSearch.vue'
import IconUser from '@/design/icons/IconUser.vue'
import IconCart from '@/design/icons/IconCart.vue'
import IconClose from '@/design/icons/IconClose.vue'
import IconExternal from '@/design/icons/IconExternal.vue'
import IconFacebook from '@/design/icons/IconFacebook.vue'
import IconInstagram from '@/design/icons/IconInstagram.vue'
import IconMenu from '@/design/icons/IconMenu.vue'
import IconChevronRight from '@/design/icons/IconChevronRight.vue'
import IconBack from '@/design/icons/IconBack.vue'

const sampleField = ref('')

const swatches = [
  ['--bg', 'bone bg'],
  ['--bg-deep', 'bg-deep'],
  ['--bg-soft', 'bg-soft'],
  ['--ink', 'ink'],
  ['--ink-soft', 'ink-soft'],
  ['--moss', 'moss'],
  ['--moss-deep', 'moss-deep'],
  ['--bark', 'bark'],
  ['--mushroom', 'mushroom'],
  ['--lichen', 'lichen'],
  ['--ember', 'ember'],
  ['--coin', 'coin']
]

const icons = [
  ['Leaf', IconLeaf], ['Fossil', IconFossil], ['Drop', IconDrop], ['Tendril', IconTendril],
  ['Shield', IconShield], ['ArrowRight', IconArrowRight], ['Copy', IconCopy], ['Check', IconCheck],
  ['Search', IconSearch], ['User', IconUser], ['Cart', IconCart], ['Close', IconClose],
  ['External', IconExternal], ['Facebook', IconFacebook], ['Instagram', IconInstagram],
  ['Menu', IconMenu], ['ChevronRight', IconChevronRight], ['Back', IconBack]
]
</script>

<template>
  <div class="preview">
    <h1 class="preview__title">Evoluxu — design system preview</h1>

    <section>
      <h2>Palette</h2>
      <div class="swatches">
        <div v-for="[v, name] in swatches" :key="v" class="swatch">
          <div class="swatch__chip" :style="{ background: `var(${v})` }"></div>
          <code>{{ v }}</code>
          <span>{{ name }}</span>
        </div>
      </div>
    </section>

    <section>
      <h2>Type</h2>
      <HeadlineFraunces level="h1">Survive. <em>Build.</em> Dominate.</HeadlineFraunces>
      <HeadlineFraunces level="h2">Section heading</HeadlineFraunces>
      <HeadlineFraunces level="h3">Subsection</HeadlineFraunces>
      <p>Body in Inter Tight at 16px / 1.55 line height. Numerals in <span style="font-family:var(--font-mono)">DM Mono 12.345</span>.</p>
    </section>

    <section>
      <h2>Buttons</h2>
      <div class="row">
        <ButtonSeed>Primary</ButtonSeed>
        <ButtonSeed variant="ghost">Ghost</ButtonSeed>
        <ButtonSeed variant="bark">Bark</ButtonSeed>
        <ButtonSeed disabled>Disabled</ButtonSeed>
      </div>
    </section>

    <section>
      <h2>Field</h2>
      <FieldLeaf v-model="sampleField" label="Display name" placeholder="Type here…"/>
      <FieldLeaf v-model="sampleField" label="Server IP" placeholder="213.193.197.191:7777" mono/>
      <FieldLeaf v-model="sampleField" label="With error" error="Something is wrong"/>
    </section>

    <section>
      <h2>Panels</h2>
      <PanelOrganic>Bone panel</PanelOrganic>
      <PanelOrganic tone="deep">Deep panel</PanelOrganic>
      <PanelOrganic tone="soft" grain>Soft panel with grain</PanelOrganic>
    </section>

    <section>
      <h2>Card</h2>
      <CardLichen>
        <strong>Card title</strong>
        <p>A subtle hoverable card.</p>
      </CardLichen>
    </section>

    <section>
      <h2>Coin glyph</h2>
      <CoinGlyph :size="24"/> <CoinGlyph :size="48"/> <CoinGlyph :size="96"/>
    </section>

    <section>
      <h2>Icons</h2>
      <div class="iconwall">
        <div v-for="[name, Icon] in icons" :key="name" class="iconcell">
          <component :is="Icon" style="width:32px;height:32px;color:var(--ink)"/>
          <span>{{ name }}</span>
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped>
.preview { max-width: var(--container-wide); margin: 0 auto; padding: var(--sp-12) var(--sp-6); display: flex; flex-direction: column; gap: var(--sp-12); }
.preview__title { font-family: var(--font-display); font-size: var(--fs-32); margin: 0; }
section { display: flex; flex-direction: column; gap: var(--sp-4); }
section h2 { font-family: var(--font-display); font-size: var(--fs-21); margin: 0; color: var(--moss); }
.swatches { display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); gap: var(--sp-3); }
.swatch { display: flex; flex-direction: column; gap: 4px; font-size: var(--fs-12); }
.swatch__chip { height: 56px; border-radius: var(--r-md); border: 1px solid var(--mushroom); }
.swatch code { font-family: var(--font-mono); }
.row { display: flex; gap: var(--sp-3); flex-wrap: wrap; }
.iconwall { display: grid; grid-template-columns: repeat(auto-fill, minmax(96px, 1fr)); gap: var(--sp-3); }
.iconcell { display: flex; flex-direction: column; align-items: center; gap: var(--sp-1); padding: var(--sp-3); border: 1px solid var(--mushroom); border-radius: var(--r-md); font-size: var(--fs-12); }
</style>
